import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeftRight, ChevronLeft, ChevronRight, Clock, Moon, Sun } from 'lucide-react';
import {
  AnnouncementBand,
  SubBar,
  EmptyState,
  ErrorState,
  Skeleton,
  StatusBadge,
} from '@/design/components';
import { cn } from '@/lib/cn';
import { supabase } from '@/lib/supabase';
import { useMe } from '@/auth/useMe';
import { qk } from '@/lib/queryKeys';
import {
  formatDate,
  timeLeftLabel,
  toDateKey,
  weekDays,
  weekStart,
  weekdayName,
} from '@/lib/dates';
import { findClosure, useClosures, useOpenPeriod, useMySubmission } from './api';
import { SwapOfferDialog } from './swaps/SwapOfferDialog';

type Assignment = {
  id: string;
  date: string;
  shift_type_id: string;
  employee_id: string;
  /** null = לפי שעות הרצועה */
  start_time: string | null;
  end_time: string | null;
  shift_types: { name: string; start_time: string; end_time: string; color: string | null } | null;
};

/** הסידור שלי (מסמך התכנון 9.2). */
export function MySchedulePage() {
  const { me } = useMe();
  const [offset, setOffset] = useState(0);

  const start = useMemo(() => {
    const base = weekStart();
    base.setDate(base.getDate() + offset * 7);
    return base;
  }, [offset]);

  const days = useMemo(() => weekDays(start), [start]);
  const from = toDateKey(days[0]);
  const to = toDateKey(days[6]);

  const week = useQuery({
    queryKey: qk.myWeek(from),
    enabled: Boolean(me),
    queryFn: async (): Promise<Assignment[]> => {
      const { data, error } = await supabase
        .from('shift_assignments')
        .select(
          'id, date, shift_type_id, employee_id, start_time, end_time, shift_types ( name, start_time, end_time, color )',
        )
        .eq('employee_id', me!.employeeId)
        .gte('date', from)
        .lte('date', to)
        .order('date');
      if (error) throw error;
      return (data ?? []) as unknown as Assignment[];
    },
  });

  const openPeriod = useOpenPeriod();
  const submission = useMySubmission(openPeriod.data?.id ?? '');
  const needsConstraints = openPeriod.data && !submission.data?.submitted_at;

  // הסניף הראשי של העובד. שם הוא עובד, ולכן שם הסגירות רלוונטיות לו
  const branchId = me?.primaryBranchId ?? me?.branches[0]?.id;
  const closures = useClosures(branchId, from, to);
  /** המשמרת שעליה פתוח דיאלוג ההצעה להחלפה */
  const [offering, setOffering] = useState<Assignment | null>(null);

  const byDate = useMemo(() => {
    const map = new Map<string, Assignment[]>();
    for (const item of week.data ?? []) {
      const list = map.get(item.date) ?? [];
      list.push(item);
      map.set(item.date, list);
    }
    return map;
  }, [week.data]);

  const today = toDateKey(new Date());

  return (
    <div className="grid content-start">
      <SubBar title="הסידור שלי" subtitle="המשמרות שלך בשבוע" />

      {needsConstraints && openPeriod.data && (
        <AnnouncementBand
          icon={Clock}
          to={`/schedule/constraints/${openPeriod.data.id}`}
          actionLabel="להגשה"
        >
          עוד לא הגשת אילוצים. {timeLeftLabel(openPeriod.data.constraints_deadline)}
        </AnnouncementBand>
      )}

      <div className="grid gap-4 p-4">
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            aria-label="שבוע קודם"
            onClick={() => setOffset((value) => value - 1)}
            className="grid size-11 place-items-center rounded-sm bg-transparent text-ink"
          >
            <ChevronRight size={24} strokeWidth={1.75} aria-hidden="true" />
          </button>

          <span className="text-body-sm text-muted tabular">
            {formatDate(days[0])} — {formatDate(days[6])}
          </span>

          <button
            type="button"
            aria-label="שבוע הבא"
            onClick={() => setOffset((value) => value + 1)}
            className="grid size-11 place-items-center rounded-sm bg-transparent text-ink"
          >
            <ChevronLeft size={24} strokeWidth={1.75} aria-hidden="true" />
          </button>
        </div>

        {week.isPending && (
          <div className="grid gap-2">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        )}

        {week.isError && <ErrorState onRetry={() => void week.refetch()} />}

        {/*
          שבוע בלי משמרות אבל עם ימים סגורים עדיין מוצג, כי "למה שבת
          נעלמה" היא השאלה שהעובד שואל דווקא כשאין לו סידור.
        */}
        {week.data && week.data.length === 0 && (closures.data?.length ?? 0) === 0 && (
          <EmptyState title="אין משמרות מתוכננות" description="הסידור הבא יפורסם בקרוב." />
        )}

        {week.data && (week.data.length > 0 || (closures.data?.length ?? 0) > 0) && (
          <ul className="grid list-none gap-2 p-0">
            {days.map((day) => {
              const key = toDateKey(day);
              const shifts = byDate.get(key) ?? [];
              const isToday = key === today;
              const closure = findClosure(closures.data, key, null);

              return (
                <li
                  key={key}
                  className={cn(
                    'flex items-center gap-3 rounded-md border p-3',
                    isToday ? 'border-olive bg-olive-soft' : 'border-line bg-raised',
                    (shifts.length === 0 || closure?.is_closed) && 'border-dashed',
                    closure?.is_closed && 'bg-charcoal/5',
                  )}
                >
                  <span className="grid w-14 shrink-0 text-center">
                    <span className="text-body-sm">{weekdayName(day)}</span>
                    <span className="text-label text-muted tabular">
                      {key.slice(8, 10)}.{key.slice(5, 7)}
                    </span>
                  </span>

                  {closure?.is_closed ? (
                    <span className="grid flex-1 gap-0.5">
                      <span className="text-body-sm text-ink">סגור — {closure.label}</span>
                      {closure.note && (
                        <span className="text-label text-muted">{closure.note}</span>
                      )}
                    </span>
                  ) : shifts.length === 0 ? (
                    <span className="grid flex-1 gap-0.5">
                      <span className="text-body-sm text-muted">יום חופש</span>
                      {closure && <span className="text-label text-muted">{closure.label}</span>}
                    </span>
                  ) : (
                    <span className="grid flex-1 gap-1">
                      {shifts.map((shift) => (
                        <span key={shift.id} className="flex items-center gap-2">
                          {isMorning(shift.shift_types?.start_time) ? (
                            <Sun size={20} strokeWidth={1.75} aria-hidden="true" />
                          ) : (
                            <Moon size={20} strokeWidth={1.75} aria-hidden="true" />
                          )}
                          <span className="text-body">{shift.shift_types?.name}</span>
                          <span className="text-body-sm text-muted tabular" dir="ltr">
                            {(shift.start_time ?? shift.shift_types?.start_time ?? '').slice(0, 5)}–
                            {(shift.end_time ?? shift.shift_types?.end_time ?? '').slice(0, 5)}
                          </span>
                          {key >= today && (
                            <button
                              type="button"
                              onClick={() => setOffering(shift)}
                              aria-label={`הצעת ${shift.shift_types?.name ?? 'המשמרת'} להחלפה`}
                              title="הצעה להחלפה"
                              className="ms-auto inline-flex min-h-9 min-w-9 cursor-pointer items-center justify-center rounded-sm bg-transparent text-muted"
                            >
                              <ArrowLeftRight size={18} strokeWidth={1.75} aria-hidden="true" />
                            </button>
                          )}
                        </span>
                      ))}
                      {closure && !closure.is_closed && (
                        <span className="text-label text-muted">{closure.label}</span>
                      )}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {openPeriod.data && submission.data?.submitted_at && (
          <Link to={`/schedule/constraints/${openPeriod.data.id}`} className="no-underline">
            <StatusBadge tone="good">הגשת אילוצים לתקופה הבאה</StatusBadge>
          </Link>
        )}
      </div>

      {offering && (
        <SwapOfferDialog
          assignmentId={offering.id}
          date={offering.date}
          shiftName={offering.shift_types?.name ?? ''}
          branchId={branchId}
          onClose={() => setOffering(null)}
        />
      )}
    </div>
  );
}

const isMorning = (time: string | undefined) => {
  if (!time) return true;
  return Number(time.slice(0, 2)) < 14;
};
